import React, { useState } from 'react';
import { useNavigate } from 'react-router-dom';
import { useTransactions, useUI, useAuth, useBankAccount } from '../hooks';
import Text from '../components/Text/Text';
import './WithdrawalScreen.css';
import LabeledValue from '../components/LabeledValue';

const WithdrawalScreen: React.FC = () => {
  const navigate = useNavigate();
  const { transactionTotals, withdrawFunds, getTransactionTotals, isLoading: isTransactionsLoading } = useTransactions();
  const { showToast } = useUI();
  const { currentUser } = useAuth();
  const { hasBankAccount, primaryBankAccount, isLoading: isBankAccountLoading } = useBankAccount();
  const [isProcessing, setIsProcessing] = useState(false);
  
  const handleWithdrawClick = async () => {
    try {
      setIsProcessing(true);
      const response = await withdrawFunds();
      showToast('success', `Withdrawal request processed successfully. ${response.processedCount} transactions processed.`);
      // Refresh transaction totals
      await getTransactionTotals();
      navigate('/home');
    } catch (error) {
      console.error('Error withdrawing funds:', error);
      showToast('error', 'Failed to process withdrawal. Please try again later.');
    } finally {
      setIsProcessing(false);
    }
  };
  
  const handleCancelClick = () => {
    navigate('/home');
  };
  
  if (isTransactionsLoading || isBankAccountLoading) {
    return (
      <div className="withdrawal-container">
        <Text className="text-h2">Withdraw Funds</Text>
        <div className="loading-state">
          <Text className="text-body">Loading withdrawal information...</Text>
        </div>
      </div>
    );
  }
  
  const withdrawableAmount = transactionTotals?.totalWithdrawable || 0;
  const hasConnectedAccount = !!currentUser?.stripeDetails?.stripeConnectedAccountId; // Check if user has a connected account
  
  return (
    <div className="withdrawal-container">
      <Text className="text-h2">Withdraw Funds</Text>
      
      <div className="withdrawal-card">
        {!hasConnectedAccount && (
          <div className="bank-account-notification">
            <Text className="notification-text">
              Bank account details are required for withdrawals.
              <a href="/profile#bank-account" className="notification-link">
                Add your bank account details
              </a>
            </Text>
          </div>
        )}
        
        {hasConnectedAccount && !hasBankAccount && (
          <div className="bank-account-notification-warning">
            <Text className="notification-text">
              Bank account details are required for withdrawals.
              <a href="/profile#bank-account" className="notification-link">
                Add your bank account details
              </a>
            </Text>
          </div>
        )}
        
        {hasConnectedAccount && hasBankAccount && primaryBankAccount && (
          <div className="info-panel">
            <Text className="info-text">
              Withdrawals will be deposited into account ending with {primaryBankAccount.last4}.
              <a href="/profile#bank-account" className="notification-link">
                Update bank account details
              </a>
            </Text>
          </div>
        )}
        
        <div className="withdrawal-amount-section">
            <LabeledValue label="Available for withdrawal" value={`$${withdrawableAmount.toFixed(2)}`} rightAligned={ true }  />
        </div>
        
        <div className="withdrawal-actions">
          <button
            className="withdraw-action-button primary-button"
            onClick={handleWithdrawClick}
            disabled={withdrawableAmount <= 0 || !hasConnectedAccount || !hasBankAccount || isProcessing}
          >
            {isProcessing ? 'Processing...' : 'Withdraw'}
          </button>
          <button 
            className="withdraw-action-button secondary-button"
            onClick={handleCancelClick}
          >
            Cancel
          </button>
        </div>
      </div>
    </div>
  );
};

export default WithdrawalScreen;